import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../services/apiClient';
import defaultNoteCover from '../assets/note-covers/default-note-cover.svg';

const coverRequests = new Map();

function fetchCoverOnce(listingId, version) {
  if (coverRequests.get(listingId)?.version !== version) {
    const request = apiClient.fetchLibraryMarketCover(listingId, { version }).catch((error) => {
      if (coverRequests.get(listingId)?.request === request) coverRequests.delete(listingId);
      throw error;
    });
    coverRequests.set(listingId, { version, request });
  }
  return coverRequests.get(listingId).request;
}

function MarketCoverImage({ listingId, hasCover, coverVersion, alt = '', className = '' }) {
  const imageRef = useRef(null);
  const [source, setSource] = useState(defaultNoteCover);

  useEffect(() => {
    setSource(defaultNoteCover);
    if (!hasCover) return undefined;
    let cancelled = false;
    let objectUrl = '';
    let observer = null;
    const load = () => fetchCoverOnce(listingId, coverVersion || '').then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setSource(objectUrl);
    }).catch(() => {
      if (!cancelled) setSource(defaultNoteCover);
    });

    if (typeof IntersectionObserver === 'function' && imageRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        load();
      }, { rootMargin: '240px', threshold: 0.01 });
      observer.observe(imageRef.current);
    } else {
      load();
    }
    return () => {
      cancelled = true;
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [hasCover, listingId, coverVersion]);

  return (
    <img
      ref={imageRef}
      src={source}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={`${className}${source === defaultNoteCover ? ' is-fallback' : ''}`.trim()}
    />
  );
}

export default MarketCoverImage;
